import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Cookie, Shield, BarChart3, Target, X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'td_capital_cookie_consent';

const cookieCategories = [
  {
    id: 'necessary',
    name: 'Strictly Necessary',
    description: 'Essential for the website to function properly. Cannot be disabled.',
    icon: Shield,
    required: true,
  },
  {
    id: 'analytics',
    name: 'Analytics & Performance',
    description: 'Help us understand how visitors interact with our website to improve user experience.',
    icon: BarChart3,
    required: false,
  },
  {
    id: 'marketing',
    name: 'Marketing & Advertising',
    description: 'Used to deliver personalized advertisements and measure their effectiveness.',
    icon: Target,
    required: false,
  },
];

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      // Small delay for better UX - let page load first
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (consentData) => {
    const consentRecord = {
      ...consentData,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentRecord));
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleDeclineAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (id) => {
    if (id === 'necessary') return; // Can't toggle necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Bottom banner — no full-screen backdrop. The banner sits above the
          page content but does NOT intercept pointer events elsewhere, so
          users can still browse while making a cookie choice. */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 animate-in slide-in-from-bottom duration-500"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl overflow-hidden">
          {!showPreferences ? (
            /* Main Banner */
            <div className="p-6 md:p-8">
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-orange-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Cookie className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">We Value Your Privacy</h3>
                  <p className="text-sm text-gray-400">Cookie Preferences</p>
                </div>
                <button
                  onClick={handleDeclineAll}
                  className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                  data-testid="cookie-close-btn"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="mb-6">
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                  By clicking "Accept All", you consent to our use of cookies. You can manage your preferences 
                  or decline non-essential cookies.
                </p>
                <a 
                  href="https://tdfxcapital.com/wp-content/uploads/2025/06/TD-Capital-Ltd-Privacy-Policy-1.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:text-primary/80 underline transition-colors"
                >
                  Read our Cookie Policy →
                </a>
              </div>

              {/* Risk Warning */}
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
                <p className="text-xs text-orange-300">
                  <strong>Risk Warning:</strong> Trading CFDs and Forex involves significant risk of loss. 
                  Please ensure you understand the risks involved.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 hover:opacity-90 text-white font-semibold h-12 rounded-xl transition-all"
                  data-testid="cookie-accept-all-btn"
                >
                  Accept All Cookies
                </Button>
                <Button
                  onClick={handleDeclineAll}
                  variant="outline"
                  className="flex-1 border-gray-500 text-white bg-gray-700 hover:bg-gray-600 h-12 rounded-xl transition-all"
                  data-testid="cookie-decline-btn"
                >
                  Decline
                </Button>
                <Button
                  onClick={() => setShowPreferences(true)}
                  variant="ghost"
                  className="flex-1 text-gray-400 hover:text-white hover:bg-gray-800 h-12 rounded-xl transition-all"
                  data-testid="cookie-manage-btn"
                >
                  Manage Preferences
                </Button>
              </div>
            </div>
          ) : (
            /* Preferences Panel */
            <div className="p-6 md:p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Cookie className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Cookie Preferences</h3>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                  data-testid="cookie-prefs-close-btn"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-4 mb-6">
                {cookieCategories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <div
                      key={category.id}
                      className="bg-gray-800/50 border border-gray-700 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="h-5 w-5 text-gray-300" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-1">
                              {category.name}
                              {category.required && (
                                <span className="ml-2 text-xs text-green-400 font-normal">(Required)</span>
                              )}
                            </h4>
                            <p className="text-xs text-gray-400 leading-relaxed">
                              {category.description}
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences[category.id]}
                          onCheckedChange={() => togglePreference(category.id)}
                          disabled={category.required}
                          className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-orange-500 data-[state=checked]:to-purple-500"
                          data-testid={`cookie-toggle-${category.id}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 hover:opacity-90 text-white font-semibold h-11 rounded-xl transition-all"
                  data-testid="cookie-save-prefs-btn"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white h-11 rounded-xl transition-all"
                  data-testid="cookie-accept-all-prefs-btn"
                >
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
